import React, { useState, useRef, useEffect, useCallback } from "react";
import { generateClient } from "aws-amplify/api";
import {
  CreateUserInput,
  getConversationInput,
  SubmitQuestion2Input,
  SubmitConversationFeedbackInput,
  GetUserKnowledgeInput,
  Team,
  UpdateTeamUserMapInput,
  User,
  UserKnowledge,
  Users,
} from "../models/API";
import RequestAccessDialog from "./RequestAccessDialog";
import {
  createUser,
  submitQuestion2,
  updateTeamUserMap,
  submitConversationFeedback,
} from "../graphql/mutations";
import {
  getConversation,
  getTeams,
  getUserKnowledge,
} from "../graphql/queries";
import ErrorModal from "./ErrorModal";
import MessageModal from "./MessageModal";
import "./ChatBoxComponent.css";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import {
  Box,
  Chip,
  Link,
  IconButton,
  Popover,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { marked, parse } from "marked";
import { useTheme } from "@mui/material/styles";
import { hexToString } from "./utils/utils";
import { useGlobal } from "../context/GlobalContext";
import { parseUserAttributes } from "./utils/userAttributes";
import ResourceSelector from "./ResourceSelector";
import PdfUploadButton from "./PdfUploadButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const THINKING_STEPS = [
  "Thinking...",
  "Analyzing your question...",
  "Searching knowledge base...",
  "Querying data sources...",
  "Generating response...",
];

// Parse knowledge ID from S3 source_id like "knowledge/t1/123.txt" or "knowledge/123.txt" (old format)
function parseKnowledgeId(sourceId: string): number | null {
  // New format: knowledge/t{team_id}/{id}.txt or knowledge/t{team_id}/{id}_ref_0.txt
  const newMatch = sourceId.match(/\/t\d+\/(\d+)(?:_ref_\d+)?\.txt$/);
  if (newMatch) return parseInt(newMatch[1], 10);
  // Old format: knowledge/{id}.txt
  const oldMatch = sourceId.match(/(\d+)(?:_ref_\d+)?\.txt$/);
  return oldMatch ? parseInt(oldMatch[1], 10) : null;
}

function ThinkingIndicator() {
  const [elapsed, setElapsed] = useState(0);
  const stepIndex = Math.min(
    Math.floor(elapsed / 5),
    THINKING_STEPS.length - 1
  );

  useEffect(() => {
    const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <CircularProgress size={16} thickness={5} />
      <span>{THINKING_STEPS[stepIndex]}</span>
      <span style={{ color: "#999", fontSize: "0.85em" }}>({elapsed}s)</span>
    </Box>
  );
}

interface ChatBoxComponentProps {
  isOpen: boolean;
  currentUserName: string;
  userConfig: User | null;
  currentTeamId: number;
  teamConfig: Team;
  conversationId: string;
  menuWidth: number;
  onConversationSaved?: () => void;
}
// @ts-ignore
const ChatBoxComponent: React.FC<ChatBoxComponentProps> = ({
  isOpen,
  currentUserName,
  userConfig,
  currentTeamId,
  teamConfig,
  conversationId,
  menuWidth,
  onConversationSaved,
}) => {
  const [showRequestAccessDialog, setShowRequestAccessDialog] = useState(false);
  const [error, setError] = useState<any>(null);
  const [message] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number>(userConfig!.id);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<
    {
      user: string;
      text: string;
      report: string;
      msgConversationId?: string;
      thinking?: string;
      pdfFilename?: string;
      sources?: any[];
      toolCalls?: any[];
    }[]
  >([]);
  const [input, setInput] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [availableSkills, setAvailableSkills] = useState<UserKnowledge[]>([]);
  const [knowledgeNameMap, setKnowledgeNameMap] = useState<
    Record<number, string>
  >({});
  const [selectedKnowledgeIds, setSelectedKnowledgeIds] = useState<number[]>(
    []
  );
  const [selectedDataSourceIds, setSelectedDataSourceIds] = useState<number[]>(
    []
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [feedback, setFeedback] = useState<
    Record<number, "up" | "down" | null>
  >({});

  const handleFeedback = async (index: number, value: "up" | "down") => {
    const newValue = feedback[index] === value ? null : value;
    setFeedback((prev) => ({ ...prev, [index]: newValue }));

    // Only call backend if setting feedback (not clearing)
    if (newValue && messages[index]?.msgConversationId) {
      try {
        const feedbackInput: SubmitConversationFeedbackInput = {
          user_id: currentUserId,
          conversation_id: messages[index].msgConversationId!,
          feedback: newValue,
        };
        console.log(
          "[Feedback] Submitting feedback:",
          JSON.stringify(feedbackInput)
        );
        console.log("[Feedback] Message index:", index, "Message data:", {
          user: messages[index].user,
          msgConversationId: messages[index].msgConversationId,
        });
        const result = await client.graphql({
          query: submitConversationFeedback,
          variables: { input: feedbackInput },
        });
        console.log("[Feedback] Backend response:", JSON.stringify(result));
      } catch (err) {
        console.error("[Feedback] Error submitting feedback:", err);
      }
    } else {
      console.log("[Feedback] Skipped backend call:", {
        newValue,
        hasMsgConversationId: !!messages[index]?.msgConversationId,
        msgConversationId: messages[index]?.msgConversationId,
        index,
      });
    }
  };

  const [pdfS3Key, setPdfS3Key] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Use refs to always have the latest values in sendMessage
  const selectedKnowledgeIdsRef = useRef<number[]>([]);
  const selectedDataSourceIdsRef = useRef<number[]>([]);
  const { globalTimeout, showDataSourceSelector, setShowDataSourceSelector } =
    useGlobal();

  // Update refs whenever state changes
  useEffect(() => {
    selectedKnowledgeIdsRef.current = selectedKnowledgeIds;
    selectedDataSourceIdsRef.current = selectedDataSourceIds;
  }, [selectedKnowledgeIds, selectedDataSourceIds]);

  // Fetch available instruction skills
  useEffect(() => {
    async function fetchSkills() {
      try {
        const input: GetUserKnowledgeInput = {
          user_id: currentUserId,
          team_id: currentTeamId,
        };
        const result = await client.graphql({
          query: getUserKnowledge,
          variables: { input },
        });
        const allKnowledge = result.data.getUserKnowledge ?? [];
        const skills = allKnowledge.filter(
          (k: UserKnowledge) => k.knowledge_type === "skill"
        );
        setAvailableSkills(skills);
        // Build knowledge name lookup map (id → name)
        const nameMap: Record<number, string> = {};
        allKnowledge.forEach((k: UserKnowledge) => {
          nameMap[k.id] = k.knowledge_name;
        });
        setKnowledgeNameMap(nameMap);
      } catch (err) {
        console.error("Failed to fetch skills:", err);
      }
    }
    if (currentUserId && currentTeamId) fetchSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, currentTeamId]);

  // Load historic messages whenever the selected conversation changes so users
  // can resume an earlier session from the history panel. For the current
  // login session (fresh start), getConversation simply returns no items.
  useEffect(() => {
    let cancelled = false;
    async function loadConversationHistory() {
      if (!currentUserId || !conversationId) return;
      try {
        const input: getConversationInput = {
          user_id: currentUserId,
          conversation_id: conversationId,
        };
        const result = await client.graphql({
          query: getConversation,
          variables: { input },
        });
        if (cancelled) return;
        const items = result.data.getConversation ?? [];
        // Preserve chronological order (the resolver already returns items in
        // ascending conversation_id / question_id order).
        const loaded: typeof messages = [];
        items.forEach((q: any) => {
          if (q.question) {
            loaded.push({
              user: "You",
              text: hexToString(q.question),
              report: "",
              msgConversationId: q.conversation_id,
            });
          }
          if (q.response) {
            let responseText = hexToString(q.response);
            let parsedSources: any[] | undefined;
            let parsedToolCalls: any[] | undefined;
            const sourcesMatch = responseText.match(
              /\n<!-- SOURCES:(.*?) -->/s
            );
            if (sourcesMatch) {
              responseText = responseText.replace(sourcesMatch[0], "");
              try {
                parsedSources = JSON.parse(sourcesMatch[1]);
              } catch {
                parsedSources = undefined;
              }
            }
            const toolCallsMatch = responseText.match(
              /\n<!-- TOOLCALLS:(.*?) -->/s
            );
            if (toolCallsMatch) {
              responseText = responseText.replace(toolCallsMatch[0], "");
              try {
                parsedToolCalls = JSON.parse(toolCallsMatch[1]);
              } catch {
                parsedToolCalls = undefined;
              }
            }
            loaded.push({
              user: "AI",
              text: responseText + "\n",
              report: "",
              msgConversationId: q.conversation_id,
              thinking: q.thinking ? hexToString(q.thinking) : undefined,
              sources: parsedSources,
              toolCalls: parsedToolCalls,
            });
          }
        });
        setMessages(loaded);
      } catch (err) {
        console.error("[ChatBox] Failed to load conversation history:", err);
      }
    }
    loadConversationHistory();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, currentUserId]);

  const handleSelectionChange = useCallback(
    (knowledgeIds: number[], dataSourceIds: number[]) => {
      setSelectedKnowledgeIds(knowledgeIds);
      setSelectedDataSourceIds(dataSourceIds);
    },
    []
  );

  const handlePdfUploaded = useCallback((s3Key: string, filename: string) => {
    setPdfS3Key(s3Key);
    setPdfFilename(filename);
  }, []);

  const handlePdfRemoved = useCallback(() => {
    setPdfS3Key(null);
    setPdfFilename(null);
  }, []);

  const handleDropdownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };
  const theme = useTheme();
  const { env } = useGlobal();
  const watermark = env.toUpperCase();
  const feedbackUrl =
    "https://form.asana.com/?k=YBdjTTPowR_mgOBaKXL7qQ&d=8442528107068";
  let client = generateClient();

  useEffect(() => {
    scrollToBottom();
    setCurrentUserId(userConfig!.id);
  }, [messages, userConfig]);

  // Load data source selector preference from user attributes
  useEffect(() => {
    if (userConfig) {
      const attrs = parseUserAttributes(userConfig);
      setShowDataSourceSelector(attrs.show_data_source_selector ?? false);
    }
  }, [userConfig, setShowDataSourceSelector]);

  const resetTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "4rem";
    }
  };

  const handleTextarea = () => {
    if (textareaRef.current) {
      resetTextarea();
      textareaRef.current.style.height = `${Math.max(
        4,
        Math.min(textareaRef.current.scrollHeight / 16, 9.4)
      )}rem`;
    }
  };

  // Export function to download AI response as HTML (answer only)
  const exportAsHTML = (msg: any, index: number) => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>FINN AI Response - ${new Date().toLocaleDateString()}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
            max-width: 900px;
            margin: 40px auto;
            padding: 20px;
            line-height: 1.6;
            color: #333;
            background-color: #f5f5f5;
        }
        .container {
            background-color: white;
            padding: 30px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .header {
            border-bottom: 2px solid #0073bb;
            padding-bottom: 15px;
            margin-bottom: 25px;
        }
        .header h1 {
            margin: 0;
            color: #0073bb;
            font-size: 24px;
        }
        .metadata {
            color: #666;
            font-size: 14px;
            margin-top: 8px;
        }
        .content {
            margin-top: 20px;
        }
        .answer {
            padding: 15px 0;
        }
        table {
            border-collapse: collapse;
            width: 100%;
            margin: 15px 0;
        }
        th, td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
        }
        th {
            background-color: #0073bb;
            color: white;
            font-weight: 600;
        }
        tr:nth-child(even) {
            background-color: #f9f9f9;
        }
        code {
            background-color: #f4f4f4;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: 'Courier New', monospace;
        }
        pre {
            background-color: #f4f4f4;
            padding: 15px;
            border-radius: 4px;
            overflow-x: auto;
        }
        .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 12px;
            color: #666;
            text-align: center;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🤖 FINN AI Assistant Response</h1>
            <div class="metadata">
                <div><strong>Date:</strong> ${new Date().toLocaleString()}</div>
                <div><strong>User:</strong> ${currentUserName}</div>
                <div><strong>Conversation ID:</strong> ${conversationId}</div>
            </div>
        </div>
        
        <div class="content">
            <div class="answer">
                ${parse(msg.text) as string}
            </div>
        </div>
        
        <div class="footer">
            Generated by FINN - SPB Finance AI Assistant | Amazon ${env.toUpperCase()} Environment
        </div>
    </div>
</body>
</html>
    `;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `FINN_Response_${
      new Date().toISOString().split("T")[0]
    }_${index}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  async function startPollingQuestion(questionId: string) {
    const INTERVAL_MS = 2000; // ms
    const MAX_ATTEMPTS = (globalTimeout * 1000) / INTERVAL_MS;

    let attempts = 0;

    while (attempts < MAX_ATTEMPTS) {
      await new Promise((r) => setTimeout(r, INTERVAL_MS));
      attempts += 1;

      try {
        const getConversationInput: getConversationInput = {
          user_id: currentUserId,
          conversation_id: questionId,
        };
        const result = await client.graphql({
          query: getConversation,
          variables: {
            input: getConversationInput,
          },
        });

        const res = result.data.getConversation;
        if (!res || res.length === 0) continue;
        let botMessage: any = null;

        if (res[0].response) {
          let responseText = hexToString(res[0].response);
          let parsedSources: any[] | undefined;
          let parsedToolCalls: any[] | undefined;

          // Extract sources from hidden marker if present
          const sourcesMatch = responseText.match(/\n<!-- SOURCES:(.*?) -->/s);
          if (sourcesMatch) {
            responseText = responseText.replace(sourcesMatch[0], "");
            try {
              parsedSources = JSON.parse(sourcesMatch[1]);
            } catch {
              parsedSources = undefined;
            }
          }

          // Extract tool calls from hidden marker if present
          const toolCallsMatch = responseText.match(
            /\n<!-- TOOLCALLS:(.*?) -->/s
          );
          if (toolCallsMatch) {
            responseText = responseText.replace(toolCallsMatch[0], "");
            try {
              parsedToolCalls = JSON.parse(toolCallsMatch[1]);
            } catch {
              parsedToolCalls = undefined;
            }
          }

          botMessage = {
            user: "AI",
            text: responseText + "\n",
            report: "",
            msgConversationId: res[0].conversation_id,
            thinking: res[0].thinking
              ? hexToString(res[0].thinking)
              : undefined,
            sources: parsedSources,
            toolCalls: parsedToolCalls,
          };
        } else {
          botMessage = {
            user: "AI",
            text:
              "Sorry, I couldn't generate a response for this request because the required data is unavailable or the system encountered an issue. Please try again later." +
              "\n",
            report: "",
          };
        }
        setMessages((prev) => prev.slice(0, -1).concat(botMessage));
        setIsLoading(false);
        onConversationSaved?.();
        return;
      } catch (e) {
        console.error("polling error", e);
        // optional: break or continue depending on your tolerance
      }
    }
    throw new Error("Timeout");
  }

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    setInput("");
    setIsLoading(true);

    const userMessage = {
      user: "You",
      text: input,
      report: "",
      msgConversationId: undefined as string | undefined,
      pdfFilename: pdfFilename || undefined,
    };
    const placeholderMessage = {
      user: "Placeholder",
      text: "Thinking...",
      report: "",
      msgConversationId: undefined as string | undefined,
    };
    setMessages([...messages, userMessage, placeholderMessage]);
    let botMessage: any = null;
    try {
      const submitQuestionInput: SubmitQuestion2Input = {
        user_id: currentUserId,
        team_id: currentTeamId,
        conversation_id: conversationId,
        question: input,
        knowledge_base_ids: selectedKnowledgeIdsRef.current,
        // When data source selector is hidden, pass undefined (= load ALL tools).
        // An empty array [] means "explicitly load no tools".
        data_source_ids: showDataSourceSelector
          ? selectedDataSourceIdsRef.current
          : undefined,
        ...(pdfS3Key && { pdf_s3_key: pdfS3Key }),
        ...(pdfFilename && { pdf_filename: pdfFilename }),
        ...(selectedSkills.length > 0 && { skills: selectedSkills }),
      };
      console.log("Submit [%o] to AI v2", submitQuestionInput);
      const result = await client.graphql({
        query: submitQuestion2,
        variables: {
          input: submitQuestionInput,
        },
      });
      console.log("Got the response [%o] from AI v2", result);
      const response = result.data.submitQuestion2;
      if (response.success) {
        await startPollingQuestion(response.question_id);
        setPdfS3Key(null);
        setPdfFilename(null);
      } else {
        botMessage = {
          user: "AI",
          text: response.message + "\n",
          report: "",
          msgConversationId: undefined as string | undefined,
        };
        setMessages((prev) => prev.slice(0, -1).concat(botMessage));
        setIsLoading(false);
      }
    } catch (error) {
      const errorMessage =
        (error as any)?.errors?.[0]?.message ?? "An unexpected error occurred";
      botMessage = {
        user: "Error",
        text:
          "Oops... I ran into some problems. Error message: " +
          errorMessage +
          "\n",
        report: "",
        msgConversationId: undefined as string | undefined,
      };
      console.error("Error calling Lambda:", error);
      setMessages((prev) => prev.slice(0, -1).concat(botMessage));
      setIsLoading(false);
      return null;
    }
  };

  marked.setOptions({
    breaks: true,
    gfm: true,
  });

  const renderMessage = (msg: any) => {
    if (msg.user === "Placeholder") {
      return <ThinkingIndicator />;
    }
    if (msg.user !== "AI") {
      return (
        <>
          {msg.pdfFilename && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mb: 1,
                p: "6px 10px",
                backgroundColor: "rgba(0, 115, 187, 0.08)",
                borderRadius: "6px",
                border: "1px solid rgba(0, 115, 187, 0.2)",
                width: "fit-content",
              }}
            >
              <PictureAsPdfIcon sx={{ fontSize: 18, color: "#d32f2f" }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: "#333",
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {msg.pdfFilename}
              </Typography>
            </Box>
          )}
          {msg.text}
        </>
      );
    }
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: (parse(msg.text) as string).replace(/>\s+</g, "><"),
        }}
      />
    );
  };

  async function fetchTeamsData() {
    try {
      const teamsData = await client.graphql({
        query: getTeams,
      });
      const teams = teamsData.data.getTeams;
      setTeams(teams);

      const createUserInput: CreateUserInput = {
        user_name: currentUserName,
      };
      const userData = await client.graphql({
        query: createUser,
        variables: {
          input: createUserInput,
        },
      });
      const currentUserId = userData.data.createUser.id;
      setCurrentUserId(currentUserId);
    } catch (err) {
      console.error(err);
    }
  }

  const openRequestAccessDialog = async () => {
    await fetchTeamsData();
    setShowRequestAccessDialog(true);
  };

  const handleRequestAccessClose = () => {
    setShowRequestAccessDialog(false);
  };

  const handleRequestAccessSave = async (item: Users) => {
    try {
      const teamUserMap: UpdateTeamUserMapInput = {
        team_id: currentTeamId,
        user_ids: item.user_ids.join(),
      };
      await client.graphql({
        query: updateTeamUserMap,
        variables: {
          input: teamUserMap,
        },
      });
    } catch (error) {
      console.error(error);
      setError(error);
    }
  };

  if (currentTeamId === 0)
    return (
      isOpen && (
        <div>
          <div>You don't have permission to access any team's data.</div>
          <button onClick={openRequestAccessDialog}>Request Access</button>
          <RequestAccessDialog
            isOpen={showRequestAccessDialog}
            onSave={handleRequestAccessSave}
            onClose={handleRequestAccessClose}
            currentUserId={currentUserId}
            initialData={teams}
            currentUserName={currentUserName}
          />
        </div>
      )
    );
  return (
    isOpen && (
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: `${menuWidth}px`,
          height: "100vh",
          overflowY: "hidden",
          "::before": {
            content: `"${watermark}"`,
            position: "absolute",
            top: "0",
            left: "middle",
            fontSize: "6.2rem",
            fontWeight: "800",
            color: "#000000d1",
            zIndex: "1",
            pointerEvents: "none",
          },
          transition: theme.transitions.create("margin-left", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <div className={`ChatBoxComponent-panel`}>
          {messages.length === 0 ? (
            /* Centered container for initial state */
            <div className="ChatBoxComponent-centered-container">
              {/* Greeting */}
              <div className="ChatBoxComponent-greeting">
                <div
                  style={{
                    color: "#000000d1",
                    fontSize: "1.6rem",
                    fontWeight: "600",
                  }}
                >
                  Hi! I'm Finn.
                </div>
                <div
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: "500",
                    marginTop: "0.6rem",
                  }}
                >
                  How can I help you today?
                </div>
              </div>

              {/* Input Container */}
              <div className="ChatBoxComponent-input-container">
                {/* Skill Selector Chips */}
                {availableSkills.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}
                  >
                    {availableSkills.map((skill) => (
                      <Chip
                        key={skill.id}
                        label={skill.knowledge_name}
                        size="small"
                        variant={
                          selectedSkills.includes(skill.knowledge_name)
                            ? "filled"
                            : "outlined"
                        }
                        color={
                          selectedSkills.includes(skill.knowledge_name)
                            ? "primary"
                            : "default"
                        }
                        onClick={() =>
                          setSelectedSkills((prev) =>
                            prev.includes(skill.knowledge_name)
                              ? prev.filter((s) => s !== skill.knowledge_name)
                              : [...prev, skill.knowledge_name]
                          )
                        }
                        sx={{ cursor: "pointer" }}
                      />
                    ))}
                  </Box>
                )}
                <div className="ChatBoxComponent-input">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      handleTextarea();
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !e.shiftKey &&
                        !e.metaKey &&
                        !e.ctrlKey &&
                        !isLoading
                      ) {
                        e.preventDefault();
                        sendMessage();
                        resetTextarea();
                      }
                    }}
                    placeholder="Ask me anything about SPB Finance data"
                  />
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <PdfUploadButton
                      userId={currentUserId}
                      onPdfUploaded={handlePdfUploaded}
                      onPdfRemoved={handlePdfRemoved}
                      disabled={isLoading}
                    />
                    {showDataSourceSelector && (
                      <IconButton
                        onClick={handleDropdownClick}
                        disabled={isLoading}
                        size="small"
                        sx={{
                          color: isLoading ? "gray" : "inherit",
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    )}
                    <button
                      className={"ChatBoxComponent-button"}
                      onClick={() => {
                        sendMessage();
                        resetTextarea();
                      }}
                      disabled={isLoading || !input.trim()}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <SendIcon />
                      )}
                    </button>
                  </div>
                </div>
                {/* Popover for ResourceSelector */}
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleDropdownClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <Box sx={{ p: 2, minWidth: "300px" }}>
                    <ResourceSelector
                      userId={currentUserId}
                      onSelectionChange={handleSelectionChange}
                    />
                  </Box>
                </Popover>
                {/* Footer */}
                <div className="ChatBoxComponent-footer">
                  <div
                    style={{
                      margin: "auto 0",
                      fontSize: "0.8rem",
                    }}
                  >
                    Press Enter to send, Shift+Enter for new line
                  </div>
                  <Link
                    href={feedbackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      fontSize: "1.2rem",
                      fontWeight: "600",
                      color: "blue",
                      opacity: "0.8",
                      "&:hover": {
                        opacity: "1",
                      },
                    }}
                  >
                    Feedback
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            /* Layout with messages */
            <>
              {/* Message Container */}
              <div className="ChatBoxComponent-message-container">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`ChatBoxComponent-message-wrapper ${msg.user}`}
                  >
                    <div className={`ChatBoxComponent-message ${msg.user}`}>
                      {/* Thinking accordion for AI messages */}
                      {msg.user === "AI" && msg.thinking && (
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            mb: 1,
                            backgroundColor: "rgba(0, 0, 0, 0.03)",
                            borderRadius: "8px",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: 36, py: 0 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#666", fontStyle: "italic" }}
                            >
                              💭 View AI thinking
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#555",
                                whiteSpace: "pre-wrap",
                                fontSize: "0.85rem",
                              }}
                            >
                              {msg.thinking}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      )}
                      {renderMessage(msg)}
                      {/* RAG Source Citations */}
                      {msg.user === "AI" &&
                        msg.sources &&
                        msg.sources.length > 0 && (
                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              mt: 1,
                              backgroundColor: "rgba(0, 115, 187, 0.04)",
                              borderRadius: "8px",
                              "&:before": { display: "none" },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ minHeight: 36, py: 0 }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#0073bb", fontWeight: 500 }}
                              >
                                📚 Sources ({msg.sources.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              {msg.sources.map((src: any, i: number) => {
                                const kid = parseKnowledgeId(
                                  src.source_id || ""
                                );
                                const name = kid
                                  ? knowledgeNameMap[kid]
                                  : undefined;
                                return (
                                  <Tooltip
                                    key={i}
                                    arrow
                                    placement="top"
                                    title={
                                      <Box sx={{ maxWidth: 320, p: 0.5 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            display: "block",
                                            mb: 0.5,
                                          }}
                                        >
                                          {name || `Knowledge #${kid || i + 1}`}
                                        </Typography>
                                        {src.chunk_excerpt && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              display: "block",
                                              color: "rgba(255,255,255,0.85)",
                                              mb: 0.5,
                                              fontStyle: "italic",
                                            }}
                                          >
                                            {src.chunk_excerpt}...
                                          </Typography>
                                        )}
                                        {src.relevance_score != null && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "rgba(255,255,255,0.7)",
                                            }}
                                          >
                                            Relevance:{" "}
                                            {(
                                              Number(src.relevance_score) * 100
                                            ).toFixed(0)}
                                            %
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                  >
                                    <Chip
                                      label={`#${kid || i + 1}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        mr: 0.5,
                                        mb: 0.5,
                                        height: "22px",
                                        fontSize: "0.7rem",
                                        borderColor: "#0073bb",
                                        color: "#0073bb",
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(0, 115, 187, 0.08)",
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      {/* Tool Calls */}
                      {msg.user === "AI" &&
                        msg.toolCalls &&
                        msg.toolCalls.length > 0 && (
                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              mt: 1,
                              backgroundColor: "rgba(156, 39, 176, 0.04)",
                              borderRadius: "8px",
                              "&:before": { display: "none" },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ minHeight: 36, py: 0 }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#9c27b0", fontWeight: 500 }}
                              >
                                🔧 Tool Calls ({msg.toolCalls.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              {msg.toolCalls.map((tc: any, i: number) => (
                                <Accordion
                                  key={i}
                                  disableGutters
                                  elevation={0}
                                  sx={{
                                    mb: 0.5,
                                    backgroundColor: "rgba(0,0,0,0.02)",
                                    borderRadius: "4px",
                                    "&:before": { display: "none" },
                                  }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ minHeight: 28, py: 0, px: 1 }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#9c27b0",
                                      }}
                                    >
                                      {tc.name}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ pt: 0, px: 1 }}>
                                    {tc.input && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: "#555",
                                            display: "block",
                                            mb: 0.25,
                                          }}
                                        >
                                          Input:
                                        </Typography>
                                        <Box
                                          component="pre"
                                          sx={{
                                            fontSize: "0.7rem",
                                            backgroundColor: "rgba(0,0,0,0.04)",
                                            p: 0.5,
                                            borderRadius: "4px",
                                            overflow: "auto",
                                            maxHeight: 150,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-all",
                                            m: 0,
                                          }}
                                        >
                                          {typeof tc.input === "string"
                                            ? tc.input
                                            : JSON.stringify(tc.input, null, 2)}
                                        </Box>
                                      </Box>
                                    )}
                                    {tc.result && (
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: "#555",
                                            display: "block",
                                            mb: 0.25,
                                          }}
                                        >
                                          Result:
                                        </Typography>
                                        <Box
                                          component="pre"
                                          sx={{
                                            fontSize: "0.7rem",
                                            backgroundColor: "rgba(0,0,0,0.04)",
                                            p: 0.5,
                                            borderRadius: "4px",
                                            overflow: "auto",
                                            maxHeight: 200,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-all",
                                            m: 0,
                                          }}
                                        >
                                          {tc.result}
                                        </Box>
                                      </Box>
                                    )}
                                    {/* Fallback for old format with input_summary */}
                                    {!tc.input && tc.input_summary && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#666",
                                          wordBreak: "break-all",
                                        }}
                                      >
                                        {tc.input_summary}
                                      </Typography>
                                    )}
                                  </AccordionDetails>
                                </Accordion>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      {/* Feedback and export buttons for AI messages */}
                      {msg.user === "AI" && (
                        <div
                          style={{
                            marginTop: "10px",
                            textAlign: "right",
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "4px",
                          }}
                        >
                          <Tooltip title="Good response">
                            <IconButton
                              size="small"
                              onClick={() => handleFeedback(index, "up")}
                              sx={{
                                color:
                                  feedback[index] === "up" ? "#2e7d32" : "#888",
                                "&:hover": {
                                  backgroundColor: "rgba(46, 125, 50, 0.1)",
                                },
                              }}
                            >
                              {feedback[index] === "up" ? (
                                <ThumbUpIcon fontSize="small" />
                              ) : (
                                <ThumbUpOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Bad response">
                            <IconButton
                              size="small"
                              onClick={() => handleFeedback(index, "down")}
                              sx={{
                                color:
                                  feedback[index] === "down"
                                    ? "#d32f2f"
                                    : "#888",
                                "&:hover": {
                                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                                },
                              }}
                            >
                              {feedback[index] === "down" ? (
                                <ThumbDownIcon fontSize="small" />
                              ) : (
                                <ThumbDownOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Export as HTML">
                            <IconButton
                              size="small"
                              onClick={() => exportAsHTML(msg, index)}
                              sx={{
                                color: "#0073bb",
                                "&:hover": {
                                  backgroundColor: "rgba(0, 115, 187, 0.1)",
                                },
                              }}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Container */}
              <div className="ChatBoxComponent-input-container">
                {/* Skill Selector Chips */}
                {availableSkills.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}
                  >
                    {availableSkills.map((skill) => (
                      <Chip
                        key={skill.id}
                        label={skill.knowledge_name}
                        size="small"
                        variant={
                          selectedSkills.includes(skill.knowledge_name)
                            ? "filled"
                            : "outlined"
                        }
                        color={
                          selectedSkills.includes(skill.knowledge_name)
                            ? "primary"
                            : "default"
                        }
                        onClick={() =>
                          setSelectedSkills((prev) =>
                            prev.includes(skill.knowledge_name)
                              ? prev.filter((s) => s !== skill.knowledge_name)
                              : [...prev, skill.knowledge_name]
                          )
                        }
                        sx={{ cursor: "pointer" }}
                      />
                    ))}
                  </Box>
                )}
                <div className="ChatBoxComponent-input">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      handleTextarea();
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !e.shiftKey &&
                        !e.metaKey &&
                        !e.ctrlKey &&
                        !isLoading
                      ) {
                        e.preventDefault();
                        sendMessage();
                        resetTextarea();
                      }
                    }}
                    placeholder="Ask me anything about SPB Finance data"
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      position: "absolute",
                      right: "1rem",
                      bottom: "50%",
                      transform: "translateY(50%)",
                      alignItems: "center",
                    }}
                  >
                    <PdfUploadButton
                      userId={currentUserId}
                      onPdfUploaded={handlePdfUploaded}
                      onPdfRemoved={handlePdfRemoved}
                      disabled={isLoading}
                    />
                    {showDataSourceSelector && (
                      <IconButton
                        onClick={handleDropdownClick}
                        disabled={isLoading}
                        size="small"
                        sx={{
                          color: isLoading ? "gray" : "inherit",
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    )}
                    <button
                      className={"ChatBoxComponent-button"}
                      onClick={() => {
                        sendMessage();
                        resetTextarea();
                      }}
                      disabled={isLoading || !input.trim()}
                      style={{ position: "static", transform: "none" }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <SendIcon />
                      )}
                    </button>
                  </div>
                </div>
                {/* Popover for ResourceSelector */}
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleDropdownClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <Box sx={{ p: 2, minWidth: "300px" }}>
                    <ResourceSelector
                      userId={currentUserId}
                      onSelectionChange={handleSelectionChange}
                    />
                  </Box>
                </Popover>
                {/* Footer */}
                <div className="ChatBoxComponent-footer">
                  <div
                    style={{
                      margin: "auto 0",
                      fontSize: "0.8rem",
                    }}
                  >
                    Press Enter to send, Shift+Enter for new line
                  </div>
                  <Link
                    href={feedbackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      fontSize: "1.2rem",
                      fontWeight: "600",
                      color: "blue",
                      opacity: "0.8",
                      "&:hover": {
                        opacity: "1",
                      },
                    }}
                  >
                    Feedback
                  </Link>
                </div>
              </div>
            </>
          )}
          <ErrorModal error={error} onClose={() => setError(null)} />
          <MessageModal message={message} />
        </div>
      </Box>
    )
  );
};

export default ChatBoxComponent;
